import { Icon } from '@iconify/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logoFull from './assets/brand/logo-full.svg';
import markOrange from './assets/brand/mark-orange.svg';
import ownerLiveHome from './assets/landing/owner-live-home.png';
import ownerMoney from './assets/landing/owner-money.png';
import ownerTasks from './assets/landing/owner-tasks.png';
import staffToday from './assets/landing/staff-today.png';
import { t } from './i18n';

/** The eight places the app can send you. */
export type BrandIconName =
  | 'home'
  | 'tasks'
  | 'quick'
  | 'staff'
  | 'routines'
  | 'money'
  | 'memory'
  | 'profile';

/**
 * The public landing page — engineered as one emotional arc, not a feature
 * list: curiosity (the promise) → pain (the old way, FELT through a pinned,
 * scroll-driven chat that piles up until it chokes) → relief (the flip) →
 * proof (the real product, screens not mockups) → aspiration (the worker's
 * side) → urgency (founding, real numbers) → the obvious next step.
 *
 * Motion is scroll-LINKED, not just scroll-triggered: the hero shot flattens
 * out of 3D as you start scrolling, the old-way stage pins for 340vh while
 * messages accumulate, story screens drift on parallax, and the final panel
 * grows to meet you. Everything collapses to a static, complete page under
 * prefers-reduced-motion.
 *
 * Every product visual is a screenshot of the live system
 * (e2e/capture-landing.mjs) — never a mockup.
 */
export function Landing() {
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
  }, []);

  return (
    <div className="min-h-[100dvh] overflow-x-clip bg-background font-sans text-foreground selection:bg-primary/20">
      <Link
        to="/signup"
        className="group flex min-h-11 items-center justify-center gap-3 bg-foreground px-4 py-2.5 text-center text-xs font-bold text-background sm:text-sm"
      >
        <span>
          <span className="text-primary">{t('Founding offer:')}</span>{' '}
          {t('50% off annual membership when you reserve now')}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-background px-3 py-1.5 text-[11px] font-black text-foreground transition-transform group-hover:translate-x-0.5 sm:text-xs">
          {t('Lock in 50%')}
          <Icon icon="ph:arrow-right-bold" className="h-3.5 w-3.5 rtl:-scale-x-100" aria-hidden />
        </span>
      </Link>

      <Nav />

      <main>
        <Hero />
        <MomentsStrip />
        <OldWay />
        <TheFlip />
        <StorySequence />
        <TwoSides />
        <MorningRhythm />
        <TranslationFlow />
        <SystemDepth />
        <FoundingAccess />
        <Questions />
        <FinalCall />
      </main>

      <Footer />
    </div>
  );
}

// ─── The scroll engine ───────────────────────────────────────────────────

type Ref<T> = { current: T | null };

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** One rAF-throttled scroll subscription pattern, shared by every effect. */
function onScrollRaf(update: () => void): () => void {
  let raf = 0;
  const handler = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  };
  update();
  window.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler);
  return () => {
    window.removeEventListener('scroll', handler);
    window.removeEventListener('resize', handler);
    cancelAnimationFrame(raf);
  };
}

/** Window scroll position (stays 0 for reduced-motion users). */
function useScrollY(): number {
  const [y, setY] = useState(0);
  useEffect(() => {
    if (reducedMotion()) return;
    return onScrollRaf(() => setY(window.scrollY));
  }, []);
  return y;
}

/**
 * Scroll progress (0→1) through a tall pinned section: 0 when its top
 * reaches the viewport top, 1 when its bottom leaves. Drives every pinned,
 * scroll-linked scene. Reduced-motion users get the final state (1).
 */
function useScrollProgress(ref: Ref<HTMLElement>): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (reducedMotion()) {
      setP(1);
      return;
    }
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      setP(total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1);
    });
  }, [ref]);
  return p;
}

/**
 * How far an element has travelled into the viewport: 0 when its top enters
 * at the bottom edge, 1 once it covers the first 55% of the screen. Powers
 * the final panel that grows to meet the visitor.
 */
function useEntranceProgress(ref: Ref<HTMLElement>): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (reducedMotion()) {
      setP(1);
      return;
    }
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setP(Math.min(1, Math.max(0, (window.innerHeight - top) / (window.innerHeight * 0.55))));
    });
  }, [ref]);
  return p;
}

/** Gentle parallax: children drift against the scroll as they cross the
 *  viewport center (±34px). Reduced-motion keeps them still. */
function Parallax({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (reducedMotion()) return;
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const center = r.top + r.height / 2 - window.innerHeight / 2;
      setOffset(Math.max(-1, Math.min(1, center / window.innerHeight)) * -34);
    });
  }, []);
  return (
    <div
      ref={ref}
      style={{ transform: `translateY(${offset}px)` }}
      className="will-change-transform"
    >
      {children}
    </div>
  );
}

/** Scroll-triggered entrance: rises and settles once, never again. */
function Reveal({
  children,
  delay = 0,
  className = '',
}: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion()) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`${className} transition-all duration-700 ease-out will-change-transform ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
    >
      {children}
    </div>
  );
}

// ─── Chrome ──────────────────────────────────────────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav
      className={`sticky top-0 z-50 border-b bg-background/85 backdrop-blur-xl transition-shadow duration-300 ${
        scrolled ? 'border-border shadow-elev-2' : 'border-transparent'
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <MarketingMark />
        <div className="hidden items-center gap-8 text-sm font-semibold text-muted-foreground lg:flex">
          <a href="#old-way" className="transition-colors hover:text-foreground">
            {t('The old way')}
          </a>
          <a href="#how" className="transition-colors hover:text-foreground">
            {t('How it works')}
          </a>
          <a href="#languages" className="transition-colors hover:text-foreground">
            {t('Languages')}
          </a>
          <a href="#questions" className="transition-colors hover:text-foreground">
            {t('Questions')}
          </a>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/login"
            className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground sm:block"
          >
            {t('Sign in')}
          </Link>
          <Link
            to="/signup"
            className="inline-flex min-h-11 items-center rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 active:scale-[0.98]"
          >
            {t('Start your household')}
          </Link>
        </div>
      </div>
    </nav>
  );
}

function MarketingMark() {
  return (
    <Link to="/" className="block shrink-0" aria-label="Famanage home" dir="ltr">
      <img src={logoFull} alt="Famanage" className="h-8 w-auto sm:h-9" draggable={false} />
    </Link>
  );
}

// ─── Act I · Curiosity — the promise, then the product in 3D ─────────────

function Hero() {
  return (
    <header className="px-4 pb-10 pt-14 text-center sm:px-6 sm:pt-20 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <p className="fm-hero-rise text-[11px] font-black uppercase tracking-[0.2em] text-primary">
          {t('For households with staff')}
        </p>
        <h1
          className="fm-hero-rise mx-auto mt-5 max-w-[14ch] font-heading text-[clamp(2.75rem,7vw,6rem)] font-bold leading-[0.95] tracking-[-0.05em]"
          style={{ animationDelay: '120ms' }}
        >
          {t('Your home should occupy your heart, not your head.')}
        </h1>
        <p
          className="fm-hero-rise mx-auto mt-7 max-w-[42rem] text-lg leading-8 text-muted-foreground sm:text-xl"
          style={{ animationDelay: '240ms' }}
        >
          {t(
            'Famanage gives your home a brain of its own — tasks, routines, receipts, approvals, and payroll, handled. The kids were picked up. The driver arrived. The housekeeper was reimbursed. And you never had to think about any of it.',
          )}
        </p>
        <div
          className="fm-hero-rise mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          style={{ animationDelay: '360ms' }}
        >
          <Link
            to="/signup"
            className="inline-flex min-h-14 w-full items-center justify-center rounded-full bg-primary px-8 text-base font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98] sm:w-auto"
          >
            {t('Start your household')}
            <Icon
              icon="ph:arrow-right-bold"
              className="ms-2 h-4 w-4 rtl:-scale-x-100"
              aria-hidden
            />
          </Link>
          <a
            href="#old-way"
            className="inline-flex min-h-14 w-full items-center justify-center rounded-full border border-border bg-card px-8 text-base font-bold transition hover:bg-secondary sm:w-auto"
          >
            {t('The old way')}
            <Icon icon="ph:arrow-down-bold" className="ms-2 h-4 w-4" aria-hidden />
          </a>
        </div>
        <p
          className="fm-hero-rise mt-4 text-sm font-medium text-muted-foreground"
          style={{ animationDelay: '440ms' }}
        >
          {t('AED 99/month, founding households · first 50 · no card today')}
        </p>
        <p
          className="fm-hero-rise mt-1 text-xs font-medium text-muted-foreground/70"
          style={{ animationDelay: '500ms' }}
        >
          {t('Less than one hour of household help per week.')}
        </p>
      </div>
      <HeroShot />
    </header>
  );
}

/** The product floats up in 3D and flattens into the page as the visitor
 *  starts scrolling — the Apple-style "the object lands" moment. */
function HeroShot() {
  const y = useScrollY();
  const settle = Math.min(1, y / 420);
  const tilt = 16 * (1 - settle);
  const scale = 0.95 + 0.05 * settle;
  return (
    <Reveal className="mx-auto mt-14 max-w-[1200px] [perspective:1600px] sm:mt-20">
      <div
        style={{
          transform: `rotateX(${tilt}deg) scale(${scale})`,
          transformOrigin: 'center 10%',
        }}
        className="will-change-transform"
      >
        <BrowserShot src={ownerLiveHome} alt="Famanage Live Home — the owner's morning screen" />
      </div>
      <figcaption className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
        {t('Real product. Real house. Running today.')}
      </figcaption>
    </Reveal>
  );
}

/** Four quiet facts, staged one after another — the rhythm of a house that
 *  runs itself. */
function MomentsStrip() {
  const moments = [
    t('The kids were picked up.'),
    t('The driver arrived.'),
    t('The housekeeper was reimbursed.'),
    t('You never had to think about any of it.'),
  ];
  return (
    <section className="px-4 pb-20 sm:px-6 sm:pb-24 lg:px-8">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-3">
        {moments.map((moment, i) => (
          <Reveal key={moment} delay={i * 120}>
            <span
              className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-bold ${
                i === moments.length - 1
                  ? 'border-primary/30 bg-primary/5 text-primary'
                  : 'border-border bg-card shadow-elev-1'
              }`}
            >
              <Icon
                icon={i === moments.length - 1 ? 'ph:sparkle-fill' : 'ph:check-circle-fill'}
                className={`h-[18px] w-[18px] ${i === moments.length - 1 ? 'text-primary' : 'text-wealth'}`}
                aria-hidden
              />
              {moment}
            </span>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

// ─── Act II · Pain — the old way, FELT ───────────────────────────────────

const CHAT: Array<{ side: 'them' | 'you'; text: string; time: string; voice?: boolean }> = [
  { side: 'you', text: 'did you pay the gardener??', time: '09:14' },
  { side: 'you', text: 'Please send the receipt photo again. I lost it.', time: '09:15' },
  { side: 'them', text: 'Voice message · 0:47', time: '09:31', voice: true },
  { side: 'them', text: 'The AC guy came but nobody told me', time: '12:03' },
  { side: 'them', text: 'what time is pickup tomorrow?', time: '21:38' },
  { side: 'you', text: 'sorry — just seeing this. was in meetings all day', time: '23:47' },
];

/**
 * The signature scene. The stage pins for 340vh; the scroll wheel itself
 * piles message after message onto the phone until the screen is crowded,
 * then the whole chat blurs and sinks while the verdict rises over it.
 * The visitor does not read about the chaos — they produce it, scroll by
 * scroll, and feel it suffocate.
 */
function OldWay() {
  const stageRef = useRef<HTMLDivElement>(null);
  const p = useScrollProgress(stageRef);
  const punchIn = Math.min(1, Math.max(0, (p - 0.78) / 0.16));
  return (
    <section id="old-way" className="border-y border-border bg-secondary">
      <div className="mx-auto max-w-7xl px-4 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-urgent">
            {t('The old way')}
          </p>
          <h2 className="mx-auto mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('This is how your home runs today.')}
          </h2>
        </Reveal>
      </div>

      <div ref={stageRef} className="relative" style={{ height: '340vh' }}>
        <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden px-4">
          {/* The phone, drowning by scroll */}
          <div
            className="w-full max-w-md will-change-transform"
            style={{
              opacity: 1 - punchIn * 0.85,
              filter: `blur(${punchIn * 7}px)`,
              transform: `scale(${1 - punchIn * 0.07}) translateY(${punchIn * 30}px)`,
            }}
          >
            <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-elev-3">
              <div className="flex items-center gap-3 border-b border-border bg-card px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                  <Icon
                    icon="ph:users-three-bold"
                    className="h-[18px] w-[18px] text-muted-foreground"
                    aria-hidden
                  />
                </span>
                <div>
                  <p className="text-sm font-bold">{t('House group')}</p>
                  <p className="text-[11px] text-muted-foreground">6 · online</p>
                </div>
              </div>
              <div className="flex h-[52vh] min-h-[340px] flex-col justify-end gap-2.5 bg-secondary/60 p-4">
                {CHAT.map((m, i) => {
                  const vis = Math.min(1, Math.max(0, (p - (0.04 + i * 0.115)) / 0.05));
                  return (
                    <div
                      key={`${m.time}-${i}`}
                      className={`flex ${m.side === 'you' ? 'justify-end' : 'justify-start'}`}
                      style={{
                        opacity: vis,
                        transform: `translateY(${(1 - vis) * 14}px)`,
                      }}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-elev-1 ${
                          m.side === 'you'
                            ? 'rounded-br-md bg-primary/10 text-foreground'
                            : 'rounded-bl-md bg-card'
                        }`}
                      >
                        <p
                          className="flex items-center gap-2 text-sm font-medium leading-snug"
                          dir="auto"
                        >
                          {m.voice && (
                            <Icon
                              icon="ph:play-circle-fill"
                              className="h-6 w-6 shrink-0 text-primary"
                              aria-hidden
                            />
                          )}
                          {t(m.text)}
                        </p>
                        <p className="mt-1 text-end text-[10px] text-muted-foreground">{m.time}</p>
                      </div>
                    </div>
                  );
                })}
                <div
                  className="flex justify-start"
                  style={{ opacity: Math.min(1, Math.max(0, (p - 0.74) / 0.05)) }}
                >
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-card px-4 py-3 shadow-elev-1">
                    <span className="fm-typing-dot" />
                    <span className="fm-typing-dot" style={{ animationDelay: '150ms' }} />
                    <span className="fm-typing-dot" style={{ animationDelay: '300ms' }} />
                    <span className="ms-1.5 text-[11px] text-muted-foreground">{t('typing…')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The verdict, rising over the drowned chat */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center px-6"
            style={{ opacity: punchIn, transform: `translateY(${(1 - punchIn) * 46}px)` }}
          >
            <div className="max-w-3xl text-center">
              <p className="font-heading text-3xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-6xl">
                {t("That's not a home. That's a company running on hope.")}
              </p>
              <p className="mx-auto mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                {t(
                  'Every day, your household spends money, runs errands and repeats routines — with no record, no memory, and no system.',
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Act III · Relief — the flip ─────────────────────────────────────────

const SIGNALS = [
  { icon: 'solar:checklist-minimalistic-bold', label: 'Work', tone: 'text-task' },
  { icon: 'solar:users-group-rounded-bold', label: 'People', tone: 'text-magic' },
  { icon: 'solar:wallet-money-bold', label: 'Money', tone: 'text-wealth' },
  { icon: 'solar:book-2-bold', label: 'Memory', tone: 'text-primary-foreground' },
];

function TheFlip() {
  return (
    <section id="calm" className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1380px] overflow-hidden rounded-card bg-foreground px-5 py-20 text-background sm:px-10 sm:py-28">
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <Reveal>
            <h2 className="max-w-[13ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
              {t('The house stops reporting problems. It presents decisions.')}
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-xl text-lg leading-8 text-background/60">
              {t(
                'Famanage stays quiet while the work gets done and speaks up only when something needs you. No dashboards, no noise, no nagging.',
              )}
            </p>
            <p className="mt-4 max-w-xl text-sm font-semibold text-background/45">
              {t(
                'Our founder’s own house has run on it for months — every task, receipt and payslip.',
              )}
            </p>
          </Reveal>
          <Reveal delay={260}>
            <span className="mt-12 flex h-24 w-24 items-center justify-center rounded-card bg-card text-foreground shadow-elev-3">
              <Icon icon="solar:home-2-bold" className="h-11 w-11" aria-hidden />
            </span>
          </Reveal>
          <div className="mt-8 grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
            {SIGNALS.map((signal, i) => (
              <Reveal key={signal.label} delay={320 + i * 90}>
                <span className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-background/15 bg-background/10 px-4 py-3 text-sm font-bold">
                  <Icon icon={signal.icon} className={`h-5 w-5 ${signal.tone}`} aria-hidden />
                  {t(signal.label)}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Act IV · Proof — the loop, on real screens ──────────────────────────

const STORY = [
  {
    label: '01 · Attention',
    title: 'Open once. Know what needs you.',
    body: 'Live Home gives you the day in one glance: the decisions waiting on you, today’s work, team status. Nothing else demands your attention.',
    shot: ownerLiveHome,
    alt: 'Live Home — decisions and running work',
  },
  {
    label: '02 · Command',
    title: 'Say it naturally. The system does the structuring.',
    body: 'Type the instruction as you would say it. Famanage identifies the person, action, timing and proof, then turns it into clear work.',
    shot: ownerTasks,
    alt: 'Structured work — every task with an owner and a state',
  },
  {
    label: '03 · Money loop',
    title: 'Photograph the receipt. Close the loop.',
    body: 'A receipt becomes an expense, approved expenses become payroll. Famanage reads the purchase and proposes the next action — you only approve.',
    shot: ownerMoney,
    alt: 'The money loop — house cost, wages, and the next decision',
  },
];

function StorySequence() {
  return (
    <section id="how" className="px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('From noise to knowing')}
          </p>
          <h2 className="mt-5 max-w-[14ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('One loop from thought to handled.')}
          </h2>
        </Reveal>
        <div className="mt-14 space-y-20 sm:space-y-28">
          {STORY.map((item, index) => (
            <article
              key={item.label}
              className="grid gap-9 lg:grid-cols-2 lg:items-center lg:gap-20"
            >
              <Reveal className={index === 1 ? 'lg:order-2' : ''}>
                <Parallax>
                  <BrowserShot src={item.shot} alt={item.alt} />
                </Parallax>
              </Reveal>
              <Reveal delay={120} className={index === 1 ? 'lg:order-1' : ''}>
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                  {t(item.label)}
                </p>
                <h3 className="mt-5 max-w-[12ch] font-heading text-3xl font-bold leading-[1.0] tracking-[-0.035em] sm:text-5xl">
                  {t(item.title)}
                </h3>
                <p className="mt-6 max-w-[36rem] text-lg leading-8 text-muted-foreground">
                  {t(item.body)}
                </p>
              </Reveal>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Act V · Aspiration — the other side of the door ─────────────────────

function TwoSides() {
  return (
    <section className="border-y border-border bg-secondary px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-24">
          <div>
            <Reveal>
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                {t('For staff')}
              </p>
              <h2 className="mt-5 font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-5xl">
                {t('Your workday, finally organized.')}
              </h2>
              <p className="mt-6 max-w-[36rem] text-lg leading-8 text-muted-foreground">
                {t(
                  'You keep the house running. You deserve better than 40 WhatsApp messages and a crumpled receipt. Your tasks for today, in your language. Routines that tell you what a perfect day looks like. Receipts you snap once and get reimbursed for — fast.',
                )}
              </p>
              <p className="mt-6 font-heading text-xl font-bold tracking-tight">
                {t("You're a professional. This is your tool.")}
              </p>
              <p className="mt-3 text-sm font-medium text-muted-foreground">
                {t(
                  'Free for staff, always. Your employer invites you; you’re set up in two minutes.',
                )}
              </p>
            </Reveal>
            <div className="mt-8 flex flex-col gap-3">
              {[
                t('Today, in your language — not 40 chat messages'),
                t('One big button per task. No guessing what "clean the house" means'),
                t('Snap a receipt once — get reimbursed fast'),
              ].map((line, i) => (
                <Reveal key={line} delay={i * 100}>
                  <div className="flex items-center gap-4 rounded-card border border-border bg-card p-5 shadow-elev-1">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Icon icon="ph:check-bold" className="text-lg text-primary" aria-hidden />
                    </span>
                    <p className="font-bold leading-snug" dir="auto">
                      {line}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={150}>
            <Parallax>
              <PhoneShot src={staffToday} alt="The staff app — Maria's day, one big button" />
            </Parallax>
          </Reveal>
        </div>
        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-border pt-8 text-center text-sm font-medium text-muted-foreground">
          <span>{t('Staff see only what they need.')}</span>
          <span>{t('Revoke access in one tap.')}</span>
          <span>{t('Your data is never sold, and never trains public AI models.')}</span>
        </div>
      </div>
    </section>
  );
}

// ─── The morning rhythm ──────────────────────────────────────────────────

function MorningRhythm() {
  return (
    <section className="px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <Reveal>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
              {t('A quiet daily rhythm')}
            </p>
            <h2 className="mt-5 max-w-[12ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
              {t('A calmer morning in under two minutes.')}
            </h2>
          </Reveal>
          <div className="divide-y divide-border border-y border-border">
            <Moment
              time="08:02"
              icon="ph:house-line-fill"
              tone="text-task"
              title={t('Open Live Home')}
              body={t(
                'See only what needs your attention, what is happening today and who is available.',
              )}
              delay={0}
            />
            <Moment
              time="08:03"
              icon="ph:magic-wand-fill"
              tone="text-magic"
              title={t('Give one natural command')}
              body={t(
                'Famanage turns your sentence into assigned, structured work with a clear next step.',
              )}
              delay={100}
            />
            <Moment
              time="08:04"
              icon="ph:check-circle-fill"
              tone="text-wealth"
              title={t('Get on with your day')}
              body={t('The team has context. You stay available for decisions, not coordination.')}
              delay={200}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Moment({
  time,
  icon,
  tone,
  title,
  body,
  delay,
}: { time: string; icon: string; tone: string; title: string; body: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <article className="grid gap-4 py-7 sm:grid-cols-[76px_52px_1fr] sm:items-start">
        <p className="pt-3 text-sm font-black text-muted-foreground/60">{time}</p>
        <span className="flex h-12 w-12 items-center justify-center rounded-row border border-border bg-card shadow-elev-1">
          <Icon icon={icon} className={`h-6 w-6 ${tone}`} aria-hidden />
        </span>
        <div>
          <h3 className="text-xl font-bold tracking-[-0.02em]">{title}</h3>
          <p className="mt-2 max-w-[38rem] leading-7 text-muted-foreground">{body}</p>
        </div>
      </article>
    </Reveal>
  );
}

// ─── Languages ───────────────────────────────────────────────────────────

const TRANSLATIONS = [
  ['English', 'Wash the car before 3 PM and send photos.'],
  ['עברית', 'שטוף את הרכב לפני 15:00 ושלח תמונות.'],
  ['اردو', 'گاڑی دوپہر 3 بجے سے پہلے دھو دیں اور تصاویر بھیجیں۔'],
  ['Tagalog', 'Hugasan ang kotse bago mag-3 PM at magpadala ng mga litrato.'],
];

function TranslationFlow() {
  return (
    <section id="languages" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-card border border-border bg-card py-16 shadow-elev-1 sm:py-24">
        <Reveal className="px-6 text-center sm:px-10">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-card bg-magic/10 text-magic">
            <Icon icon="solar:translation-bold" className="h-7 w-7" aria-hidden />
          </span>
          <h2 className="mx-auto mt-7 max-w-[12ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Say it once. Everyone understands.')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(
              'Four languages are not a settings menu. They are how one household works as one team.',
            )}
          </p>
        </Reveal>
        <div className="no-scrollbar mt-14 flex gap-4 overflow-x-auto px-6 sm:flex-wrap sm:justify-center sm:px-10">
          {TRANSLATIONS.map(([language, copy], i) => (
            <Reveal key={language} delay={i * 90}>
              <div className="w-[300px] shrink-0 rounded-card border border-border bg-secondary p-6">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">
                  {language}
                </p>
                <p className="mt-4 text-lg font-bold leading-8" dir="auto">
                  {copy}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Depth — people, money, memory ───────────────────────────────────────

function SystemDepth() {
  return (
    <section id="system" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('One household system')}
          </p>
          <h2 className="mt-5 max-w-[15ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Not another to-do app. The operating layer for your home.')}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-5 lg:grid-cols-2">
          <ModuleCard
            tone="magic"
            icon="solar:users-group-rounded-bold"
            eyebrow={t('People')}
            title={t('Built for the worker, not the manager.')}
            body={t(
              'A housekeeper with broken English opens one big button in her own language — Hebrew, Urdu or Tagalog, right-to-left — and just works. Owners see proof instead of chasing.',
            )}
            points={[
              t('One big button, zero training'),
              t('Clear status and photo proof'),
              t('Leave, pay and responsibilities'),
            ]}
          />
          <ModuleCard
            tone="wealth"
            icon="solar:wallet-money-bold"
            eyebrow={t('Money')}
            title={t('Receipt in, payroll out.')}
            body={t(
              'Receipts become expenses, approved expenses become payroll, and the loop closes itself. Cash, loans and reimbursements live alongside the work that created them.',
            )}
            points={[
              t('Receipt capture and review'),
              t('Cash and reimbursement records'),
              t('A complete household money trail'),
            ]}
          />
          <ModuleCard
            wide
            dark
            tone="task"
            icon="solar:book-2-bold"
            eyebrow={t('Memory')}
            title={t('The home remembers how you like things done.')}
            body={t(
              'Standards, preferences, vendor rules and recurring protocols stop living inside one person’s head. The house learns what it needs — and speaks up only when it’s useful.',
            )}
            points={[
              t('Household standards'),
              t('Vendor and purchase preferences'),
              t('Reusable routines and protocols'),
            ]}
          />
        </div>
      </div>
    </section>
  );
}

/** Module tones as literal class strings so Tailwind sees every one of them. */
const TONE_CLASSES = {
  magic: { dot: 'bg-magic', text: 'text-magic', tile: 'bg-magic/10 text-magic' },
  wealth: { dot: 'bg-wealth', text: 'text-wealth', tile: 'bg-wealth/10 text-wealth' },
  task: { dot: 'bg-task', text: 'text-task', tile: 'bg-task/10 text-task' },
} as const;

function ModuleCard({
  tone,
  icon,
  eyebrow,
  title,
  body,
  points,
  wide = false,
  dark = false,
}: {
  tone: keyof typeof TONE_CLASSES;
  icon: string;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  wide?: boolean;
  dark?: boolean;
}) {
  const toneClass = TONE_CLASSES[tone];
  return (
    <Reveal className={wide ? 'lg:col-span-2' : ''}>
      <article
        className={`relative h-full overflow-hidden rounded-card border p-7 transition-transform duration-300 hover:-translate-y-1 sm:p-10 ${wide ? 'lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:gap-16' : ''} ${dark ? 'border-transparent bg-foreground text-background' : 'border-border bg-card text-foreground shadow-elev-1'}`}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <span className={`h-2.5 w-2.5 rounded-full ${toneClass.dot}`} />
            <p className={`text-[11px] font-black uppercase tracking-[0.2em] ${toneClass.text}`}>
              {eyebrow}
            </p>
          </div>
          <h3 className="mt-7 max-w-[14ch] font-heading text-3xl font-bold leading-[1.0] tracking-[-0.035em] sm:text-5xl">
            {title}
          </h3>
          <p
            className={`mt-5 max-w-[40rem] text-lg leading-8 ${dark ? 'text-background/60' : 'text-muted-foreground'}`}
          >
            {body}
          </p>
        </div>
        <div className={`relative z-10 mt-12 ${wide ? 'lg:mt-0 lg:self-end' : ''}`}>
          <span
            className={`mb-9 flex h-16 w-16 items-center justify-center rounded-row ${dark ? 'bg-card/10' : ''} ${toneClass.tile}`}
          >
            <Icon icon={icon} className="h-8 w-8" aria-hidden />
          </span>
          <ul className={`divide-y ${dark ? 'divide-background/15' : 'divide-border'}`}>
            {points.map((point) => (
              <li key={point} className="flex items-center gap-3 py-3 text-sm font-semibold">
                <Icon icon="ph:check-bold" className={`h-4 w-4 ${toneClass.text}`} aria-hidden />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </article>
    </Reveal>
  );
}

// ─── Act VI · Urgency — founding, real numbers ───────────────────────────

function FoundingAccess() {
  return (
    <section id="founding" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto grid max-w-7xl overflow-hidden rounded-card border border-border bg-card shadow-elev-2 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="p-7 sm:p-12 lg:p-16">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-urgent">
            {t('Founding access')}
          </p>
          <h2 className="mt-5 max-w-[13ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('The first fifty households. AED 99, locked for life.')}
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">
            {t(
              'Join the first households shaping Famanage. Personal setup is included, and no card is required today.',
            )}
          </p>
          <Link
            to="/signup"
            className="mt-9 inline-flex min-h-14 items-center rounded-full bg-primary px-8 text-base font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98]"
          >
            {t('Start your household')}
            <Icon
              icon="ph:arrow-right-bold"
              className="ms-2 h-4 w-4 rtl:-scale-x-100"
              aria-hidden
            />
          </Link>
        </div>
        <div className="relative flex min-h-[380px] flex-col justify-between border-t border-border bg-secondary p-7 sm:p-12 lg:border-s lg:border-t-0 lg:p-16">
          <div>
            <p className="text-sm font-bold text-muted-foreground">{t('Launch membership')}</p>
            <p className="mt-3 font-heading text-6xl font-bold tracking-[-0.05em] sm:text-7xl">
              AED 99
            </p>
            <p className="mt-2 text-base font-semibold text-muted-foreground">
              {t('per month at launch')}
            </p>
          </div>
          <div className="mt-14 space-y-4">
            {[
              t('Personal household setup'),
              t('Four languages built in'),
              t('You pay only when your household goes live.'),
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 text-sm font-bold">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-card text-wealth shadow-elev-1">
                  <Icon icon="ph:check-bold" className="h-4 w-4" aria-hidden />
                </span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Questions ───────────────────────────────────────────────────────────

function Questions() {
  return (
    <section id="questions" className="border-t border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:gap-24">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Questions')}
          </p>
          <h2 className="mt-5 font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Before you bring it home.')}
          </h2>
        </Reveal>
        <div className="border-t border-border">
          <Faq question={t('Who is Famanage for?')}>
            {t(
              'Households with staff — a housekeeper, a driver, a nanny, a cook. If you run your home on your own, you don’t need us.',
            )}
          </Faq>
          <Faq question={t('Is Famanage a smart-home controller?')}>
            {t(
              'No. It is the operating system for the people and work behind your home: tasks, routines, expenses, pay, household knowledge and communication.',
            )}
          </Faq>
          <Faq question={t('How does my household team get access?')}>
            {t(
              'The owner creates each staff account and controls what that person can see. Staff get one big button and a focused Today view — in their own language, right-to-left included.',
            )}
          </Faq>
          <Faq question={t('Does it work offline?')}>
            {t('Yes. The work of the house doesn’t stop when the internet does.')}
          </Faq>
          <Faq question={t('Which languages are supported?')}>
            {t(
              'English, Hebrew, Urdu and Tagalog are built into the product so instructions can reach each person in a language they understand.',
            )}
          </Faq>
          <Faq question={t('What does founding access include?')}>
            {t(
              'Early households receive personal setup, the AED 99/month launch price and 50% off annual membership when they reserve now. Requesting access today does not require a card.',
            )}
          </Faq>
        </div>
      </div>
    </section>
  );
}

function Faq({ question, children }: { question: string; children: ReactNode }) {
  return (
    <details className="group border-b border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-7 text-lg font-bold marker:content-none">
        <span>{question}</span>
        <Icon
          icon="ph:plus-bold"
          className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
          aria-hidden
        />
      </summary>
      <p className="max-w-[42rem] pb-7 pe-10 leading-7 text-muted-foreground">{children}</p>
    </details>
  );
}

// ─── Act VII · The obvious next step — the panel grows to meet you ───────

function FinalCall() {
  const ref = useRef<HTMLDivElement>(null);
  const p = useEntranceProgress(ref);
  return (
    <section className="px-4 pb-6 sm:px-6 lg:px-8">
      <div
        ref={ref}
        className="mx-auto flex min-h-[520px] max-w-[1380px] flex-col items-center justify-center overflow-hidden rounded-card bg-primary px-5 py-20 text-center text-primary-foreground will-change-transform sm:px-10"
        style={{
          transform: `scale(${0.92 + p * 0.08})`,
          opacity: 0.35 + p * 0.65,
        }}
      >
        <img
          src={markOrange}
          alt=""
          className="h-11 w-auto brightness-0 invert"
          draggable={false}
        />
        <h2 className="mt-8 max-w-[14ch] font-heading text-4xl font-bold leading-[0.95] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
          {t('Your home already runs. Let it run itself.')}
        </h2>
        <p className="mt-7 max-w-xl text-lg leading-8 text-primary-foreground/75">
          {t('Join the founding release and help shape the calmest way to run a home.')}
        </p>
        <Link
          to="/signup"
          className="mt-9 inline-flex min-h-14 items-center rounded-full bg-background px-8 text-base font-bold text-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98]"
        >
          {t('Start your household')}
        </Link>
        <p className="mt-4 text-sm font-medium text-primary-foreground/70">
          {t('AED 99/month at launch · no card today')}
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <MarketingMark />
          <p className="mt-4 max-w-sm text-sm leading-6 text-muted-foreground">
            {t('Freedom from managing your own house.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-muted-foreground">
          <a href="#how" className="hover:text-foreground">
            {t('How it works')}
          </a>
          <a href="#questions" className="hover:text-foreground">
            {t('Questions')}
          </a>
          <a href="mailto:hello@famanage.com" className="hover:text-foreground">
            {t('Contact')}
          </a>
          <Link to="/login" className="hover:text-foreground">
            {t('Sign in')}
          </Link>
          <Link to="/staff/login" className="hover:text-foreground">
            {t('Staff?')} {t('Sign in here')}
          </Link>
        </div>
      </div>
      <div className="mx-auto mt-10 max-w-7xl border-t border-border pt-6 text-xs font-medium text-muted-foreground/70">
        © 2026 Famanage
      </div>
    </footer>
  );
}

// ─── Product visuals — real screens, in honest frames ────────────────────

/** A desktop screenshot inside a browser frame: three dots and the URL.
 *  The frame says "this is the actual app" without a word of copy. */
function BrowserShot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="overflow-hidden rounded-card border border-border bg-card shadow-elev-3 transition-transform duration-500 hover:scale-[1.01]">
      <div
        className="flex items-center gap-1.5 border-b border-border bg-secondary px-4 py-2.5"
        dir="ltr"
      >
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="mx-auto flex w-56 items-center justify-center gap-1.5 rounded-full bg-card px-3 py-1 text-[10px] font-semibold text-muted-foreground">
          <Icon icon="ph:lock-bold" className="h-3 w-3" aria-hidden />
          app.famanage.com
        </span>
      </div>
      <img src={src} alt={alt} className="w-full" loading="lazy" draggable={false} />
    </figure>
  );
}

/** The staff app, framed like the phone it actually runs on. */
function PhoneShot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="mx-auto max-w-[340px] overflow-hidden rounded-[3rem] border border-border bg-foreground p-2.5 shadow-elev-3 transition-transform duration-500 hover:scale-[1.02]">
      <div className="overflow-hidden rounded-[2.4rem] bg-background">
        <img src={src} alt={alt} className="w-full" loading="lazy" draggable={false} />
      </div>
    </figure>
  );
}

/** The name, set in type. */
export function Wordmark({
  className = '',
  compact = false,
}: { className?: string; compact?: boolean }) {
  return (
    <div className={`flex items-center ${className}`} dir="ltr">
      <span
        className={
          compact
            ? 'text-base font-bold tracking-tight text-foreground'
            : 'text-3xl font-bold tracking-tight text-foreground'
        }
      >
        famanage
      </span>
    </div>
  );
}

const DESTINATION_ICON: Record<BrandIconName, string> = {
  home: 'ph:house-fill',
  tasks: 'ph:check-square-bold',
  quick: 'ph:sparkle-fill',
  staff: 'ph:users-three-bold',
  routines: 'ph:clock-counter-clockwise-bold',
  money: 'ph:currency-circle-dollar-bold',
  memory: 'ph:book-open-bold',
  profile: 'ph:user-bold',
};

export function BrandIcon({ name, className = '' }: { name: BrandIconName; className?: string }) {
  return <Icon icon={DESTINATION_ICON[name]} className={className} aria-hidden />;
}

const MAJOR_VERSION = 'v1';
export function BuildChip({ className = '' }: { className?: string }) {
  const id = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
  return (
    <span
      title={`${MAJOR_VERSION} · ${id}`}
      className={`select-none rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground ${className}`}
      dir="ltr"
    >
      {MAJOR_VERSION} · {id}
    </span>
  );
}
